import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from '../../../../hooks';
import { getCampanhas } from '../services';
import { useCampaignFilters } from './useCampaignFilters';

const sortColumnsTypes = {
  name: 'name',
  update_date: 'updateDate',
  starting_date: 'startingDate',
  created_date: 'createdDate',
  expiration_date: 'expirationDate',
  author: 'author',
  status: 'status',
  campaign_type: 'priorityOrder',
};

export type SortType = keyof typeof sortColumnsTypes;

export const useCampaign = () => {
  const { handlers, states } = useCampaignFilters();
  const [campaignName, setCampaignName] = useState('');
  const [searchCampaignName, setSearchCampaignName] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [sort, setSort] = useState<{
    type: SortType;
    orderBy: 'asc' | 'desc';
  }>({
    type: 'name',
    orderBy: 'asc',
  });

  const debounce = useDebounce(400);

  const params = {
    name: searchCampaignName,
    page: page || null,
    size: size || null,
    authors: states.values.responsibles || null,
    storeIds: states.values.storeIds || null,
    clusters: states.values.clusters || null,
    status: states.values.status || null,
    productIds: states.values.productIds || null,
    campaigns: states.values.campaigns || null,
    sort: sort?.type && sort?.orderBy ? `${sortColumnsTypes[sort.type]},${sort.orderBy}` : null,
  };

  const onSearchCampaignName = useCallback(
    (query: string) => {
      debounce(() => setSearchCampaignName(query));
    },
    [debounce],
  );

  useEffect(() => {
    onSearchCampaignName(campaignName);
  }, [campaignName, onSearchCampaignName]);

  const { data: campaigns } = useQuery({
    queryKey: ['campaigns', params],
    queryFn: () => getCampanhas(params),
    onSuccess(data) {
      setPage(data?.number || 0);
    },
  });

  return {
    filters: {
      ...states,
      ...handlers,
    },
    states: {
      campaignName,
      campaigns,
      sort,
    },
    handlers: {
      setCampaignName,
      setSort,
      setPage,
      setSize,
    },
  };
};
